import { useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { safeCloseMicroModalById } from "@/hooks/chat/helpers/safeMicroModal";
import styles from "./index.module.css";
import Button from "@/bricks/Button";
import { trackEvent } from "@/js/helpers/tracking";
import { FuelContext } from "@/providers/fuel-provider";
import { resolveProactiveModalPresentation } from "@/js/helpers/proactiveModalDisplay";
import { mergeProactiveModalContent } from "@/hooks/chat/helpers/mergeProactiveModalContent";
import { injectLegalAndTooltips } from "@/js/utils/legal";
import { triggerChatbotClick } from "@/hooks/chat/helpers/chatbotHelpers";
import { handleProactiveMicroModalDidClose } from "@/hooks/chat/helpers/chatTriggerCoordinator";
import VerizonFrontierLogo from "@/blocks/Icons/VerizonFrontierLogo";

const trackDismiss = () => {
  trackEvent({
    action: "elementClicked",
    data: {
      elementType: "BUTTON",
      position: "PROACTIVE CHAT MODAL",
      text: "Close",
    },
  });
};

const ProactiveChatModal = () => {
  const dialogRef = useRef(null);
  /** Only true while `<dialog>` is open — avoids `body:has([aria-modal="true"])` (DetailsModal CSS) matching when closed. */
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const { chatConfig } = useContext(FuelContext);
  const triggerOverrides = useSelector((state) => state.chat.triggerOverrides);

  const resolvedPmc = triggerOverrides?.proactiveModalContent
    ? mergeProactiveModalContent(
        chatConfig?.proactiveModalContent,
        triggerOverrides.proactiveModalContent,
      )
    : chatConfig?.proactiveModalContent;

  const {
    heading,
    subheading,
    featureHighlights,
    steps,
    helperText,
    ctaCopy,
    ctaButtonStyle,
    darkMode,
    showHeaderBrand,
  } = resolveProactiveModalPresentation(resolvedPmc);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;

    const syncOpen = () => {
      setDialogIsOpen(el.open);
    };

    syncOpen();

    const onClose = () => {
      document.body.style.overflow = "";
      handleProactiveMicroModalDidClose();
      setDialogIsOpen(false);
    };

    el.addEventListener("close", onClose);

    const mo = new MutationObserver(syncOpen);
    mo.observe(el, { attributes: true, attributeFilter: ["open"] });

    return () => {
      el.removeEventListener("close", onClose);
      mo.disconnect();
    };
  }, []);

  const handleStartChat = (e) => {
    safeCloseMicroModalById();

    trackEvent({
      action: "elementClicked",
      data: {
        elementType: "BUTTON",
        position: "PROACTIVE CHAT MODAL",
        text: e.target.textContent.trim(),
      },
    });

    triggerChatbotClick();
  };

  const dismissFromUser = () => {
    safeCloseMicroModalById();
    trackDismiss();
  };

  const handleCloseButtonClick = (e) => {
    e.stopPropagation();
    dismissFromUser();
  };

  return (
    <section className={classNames("component", styles.component)}>
      <dialog
        ref={dialogRef}
        id="modal-proactive-chat"
        className={styles.component__dialog}
        aria-modal={dialogIsOpen ? "true" : undefined}
        aria-hidden={dialogIsOpen ? undefined : "true"}
        aria-labelledby="proactive-chat-modal-title"
      >
        <div
          className={styles.component__dialogScrim}
          onClick={dismissFromUser}
        >
          <div
            className={classNames(
              styles.component__dialogPanel,
              "modal__container",
              styles.component__modalContainer,
              darkMode && styles.component__modalContainer_dark,
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.component__modalChrome}>
              <button
                type="button"
                className={classNames(
                  "modal__close",
                  styles.component__close,
                  darkMode && styles.component__close_dark,
                )}
                aria-label="Close modal"
                onClick={handleCloseButtonClick}
              />
            </div>
            <div
              className={classNames(
                "modal__content",
                styles.component__body,
                darkMode && styles.component__body_dark,
              )}
              id="modal-proactive-chat-content"
            >
              {showHeaderBrand && (
                <span
                  className={styles.component__frontierLogo}
                  role="img"
                  aria-label="Verizon Frontier logo"
                >
                  <VerizonFrontierLogo aria-hidden />
                </span>
              )}

              <h2
                className={styles.component__header}
                id="proactive-chat-modal-title"
              >
                {injectLegalAndTooltips(heading || "Need help?")}
              </h2>
              <p className={styles.component__text}>
                {injectLegalAndTooltips(
                  subheading ||
                    "Our specialists are here to answer your questions about plans, installation and more.",
                )}
              </p>

              {featureHighlights.length > 0 && (
                <ul className={styles.component__list}>
                  {featureHighlights.map(({ iconClass, copy }, idx) => (
                    <li key={`${idx}-${iconClass}-${copy}`}>
                      <i
                        className={classNames(
                          styles.component__icon,
                          iconClass,
                        )}
                        aria-hidden
                      />
                      {injectLegalAndTooltips(copy)}
                    </li>
                  ))}
                </ul>
              )}

              {steps.length > 0 && (
                <ul className={styles.component__steps}>
                  {steps.map((line, idx) => (
                    <li key={`${idx}-${line}`}>
                      {injectLegalAndTooltips(line)}
                    </li>
                  ))}
                </ul>
              )}

              {helperText && (
                <p className={styles.component__helperText}>
                  {injectLegalAndTooltips(helperText)}
                </p>
              )}

              <Button
                className={styles.component__button}
                style={ctaButtonStyle}
                onClick={handleStartChat}
              >
                {injectLegalAndTooltips(ctaCopy || "Start a live chat")}
              </Button>
            </div>
          </div>
        </div>
      </dialog>
    </section>
  );
};

export default ProactiveChatModal;
